/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable complexity */
import { FieldOptions } from '../../helper/form_builder/form_builder_iterfaces';
import { RootState } from '../../redux/store';
import { useSelector } from 'react-redux';
import { Box, Button, InputAdornment, Rating, Slider, TextField, Typography } from '@mui/material';
import { CloudUpload, InsertPhoto } from '@mui/icons-material';

interface RenderFormFieldsProps {
    field: FieldOptions;
    touched?: { [key: string]: unknown };
    errors?: null | { [key: string]: unknown };
    handleBlur: (value: string) => void;
    handleFieldChange?: (fieldId: string, value: string | number | null | number[] | File | undefined) => void;
    formData?: Record<string, unknown>;
}

const getInputProps = (field: FieldOptions) => {
    switch (field.type) {
        case 'percentage':
            return { endAdornment: <InputAdornment position="end">%</InputAdornment> };
        case 'currency':
            return { startAdornment: <InputAdornment position="start">{field.label}</InputAdornment> };
        default:
            return {};
    }
};

const RenderFormFields = ({ field, touched, errors, handleBlur, handleFieldChange, formData }: RenderFormFieldsProps) => {
    const { preview: isPreview } = useSelector((state: RootState) => state.form);

    // ✅ Simplified error handling
    const error = field.id && touched && touched[field.id] ? errors?.[String(field.id)] ?? null : null;

    // ✅ Standardized commonProps
    const commonProps = {
        error: Boolean(error),
        onBlur: () => isPreview && field.id && handleBlur(field.id),
        disabled: !isPreview,
        required: field.required,
        fullWidth: true
    };

    // ✅ Common text-based field rendering function
    const renderTextField = (type = 'text', multiline = false, rows = 1) => (
        <TextField
            {...commonProps}
            type={type}
            multiline={multiline}
            rows={rows}
            placeholder={field.placeholder}
            value={field.id && formData ? formData[field.id] : ''}
            size="small"
            onChange={(e) => handleFieldChange && handleFieldChange(field.id || '', e.target.value)}
            // helperText={error}
            InputProps={getInputProps(field)}
        />
    );
    // const renderHeading = () => (
    //     field.heading_size === 'h1'
    //         ? <h1>{field.label}</h1>
    //         : field.heading_size === 'h2'
    //             ? <h2>{field.label}</h2>
    //             : field.heading_size === 'h3'
    //                 ? <h3>{field.label}</h3>
    //                 : field.heading_size === 'h4'
    //                     ? <h4>{field.label}</h4>
    //                     : field.heading_size === 'h5'
    //                         ? <h5>{field.label}</h5>
    //                             : <h6>{field.label}</h6>
    // );

    const renderButton = () => {
        return <Box display="flex" width="100%" sx={{ justifyContent: field.btnAlign === 'right' ? 'end' : field.btnAlign === 'center' ? 'center' : 'start' as string }}><Button variant="contained" sx={{
            bgcolor: field.bgColor || 'black',
            color: field.labelColor || 'white',
            width: '25%',
            fontSize: field.btnSize === 'small' ? '16px' : field.btnSize === 'medium' ? '18px' : field.btnSize === 'large' ? '20px' : '22px',
            padding: field.btnSize === 'small' ? '10px' : field.btnSize === 'medium' ? '12px' : field.btnSize === 'large' ? '14px' : '16px'
        }}>{field.label}</Button></Box>;
    };

    switch (field.type) {
        case 'text':
        case 'email':
        case 'phone':
            return renderTextField(field.type);
        case 'heading':
            return <p>{field && (field.sub_heading_text as string || '')}</p>;
        case 'submit':
            return renderButton();

        case 'multiline':
            return renderTextField('text', true, 4);

        case 'decimal':
        case 'percentage':
        case 'number':
            return renderTextField('number');

        case 'currency':
            return renderTextField('number');

        case 'date':
            return renderTextField('date');
        case 'rating':
            return (
                <>
                    <Rating
                        max={field.maxStars}
                        disabled={!isPreview}
                        value={formData && field.id ? Number(formData[field.id || '']) || 0 : 0}
                        onChange={(e, newValue) => handleFieldChange && handleFieldChange(field.id, newValue)}
                        onBlur={() => handleBlur(field.id)}
                    />
                    {/* {error && <FormHelperText error>{error}</FormHelperText>} */}
                </>
            );

        case 'slider':
            return (
                <>
                    <Slider
                        min={field.min}
                        max={field.max}
                        step={field.step as number}
                        disabled={!isPreview}
                        value={formData && field.id ? Number(formData[field.id]) || field.min : 1}
                        onChange={(e, newValue) => handleFieldChange && handleFieldChange(field.id, newValue)}
                        onBlur={() => handleBlur(field.id)}
                        valueLabelDisplay="auto"
                    />
                </>
            );

        case 'checkbox':
            return (
                <>
                    {(field.options as string[])?.map((option: any, idx: number) => (
                        <label key={idx} className="flex items-center mb-2">
                            <input
                                type="checkbox"
                                disabled={!isPreview}
                                checked={formData && field.id && formData[field.id] ? (formData[field.id] as string).includes(option) : false}
                                onChange={(e) => {
                                    const currentValues = (formData ? formData[field.id] as string[] : ['']) ?? [];
                                    const newValues = e.target.checked
                                        ? [...currentValues, option]
                                        : currentValues.filter((v) => v !== option);
                                    if (handleFieldChange) {
                                        handleFieldChange(field.id, newValues);
                                    }
                                }}
                                onBlur={() => handleBlur(field.id)}
                            />
                            {option}
                        </label>
                    ))}
                </>
            );

        case 'radio':
            return (
                <>
                    {(field.options as string[])?.map((option: any, idx: number) => (
                        <label key={idx} className="flex items-center mb-2">
                            <input
                                type="radio"
                                name={field.id}
                                disabled={!isPreview}
                                checked={formData && field.id ? formData[field.id] === option : false}
                                onChange={() => handleFieldChange && handleFieldChange(field.id, option)}
                                onBlur={() => handleBlur(field.id)}
                            />
                            {option}
                        </label>
                    ))}
                </>
            );

        case 'file':
        case 'image':
            return (
                <>
                    <Button
                        variant="contained"
                        component="label"
                        startIcon={field.type === 'image' ? <InsertPhoto /> : <CloudUpload />}
                        disabled={!isPreview}
                    >
                        Upload {field.type === 'image' ? 'Image' : 'File'}
                        <input
                            type="file"
                            hidden
                            accept={field.acceptedTypes}
                            onChange={(e) => handleFieldChange && handleFieldChange(field.id, e.target.files?.[0])}
                            onBlur={() => handleBlur(field.id)}
                        />
                    </Button>
                    {formData && formData[field.id] && (
                        <Typography variant="body2" className="mt-2">
                            {/* Selected: {formData[field.id]?.name} */}
                        </Typography>
                    )}
                </>
            );

        // case 'submit':
        //     return (
        //         <Button
        //             variant="contained"
        //             color="primary"
        //             type="submit"
        //             disabled={!isPreview}
        //             onClick={() => handleFieldChange && handleFieldChange(field.id, '')}
        //         >
        //             Submit
        //         </Button>
        //     );
        default:
            return <></>;
    }
};

export default RenderFormFields;